import { CSSProperties, RefObject, useEffect, useState } from 'react';

// On iOS the store app keeps the web view at full size when the on-screen
// keyboard opens (capacitor.config.ts, Keyboard resize "none"), and so does
// Android 15 and later (edge to edge), so the keyboard draws on top of the
// page and nothing moves out of its way. On a tablet in landscape it covers
// the lower half of the auth card: the password field and the main button end
// up under it. Android 14 and earlier shrink the web view for the keyboard
// instead, so only the part of the keyboard that still overlaps the view
// counts. Capacitor's Keyboard plugin reports the keyboard's height through
// these window events on iOS and Android; a plain browser never sends them, so
// the web site is unaffected.
//
// Only keyboardDidShow is used: keyboardWillShow over-reports on iPad (by the
// web view's top offset), which would move the page too far and then back.
const KEYBOARD_SHOWN_EVENT = 'keyboardDidShow';
// Only once the keyboard is fully gone: a tap on the page's button takes focus
// from the field and starts the keyboard closing, and the tap's click arrives
// after that. Moving the page back down any earlier would move the button out
// from under the finger and lose the tap.
const KEYBOARD_GONE_EVENT = 'keyboardDidHide';

/** Gap kept between the keyboard and what sits just above it, and above the top edge. */
export const KEYBOARD_CLEARANCE_PX = 16;

interface KeyboardHeightEvent extends Event {
  keyboardHeight?: number;
}

export interface ViewportSize {
  width: number;
  height: number;
}

export interface KeyboardLiftInput {
  /** Top and bottom of the focused field, as drawn now (current lift included). */
  focusedTop: number;
  focusedBottom: number;
  /** Bottom of the focused field's form button, as drawn now; null when there is none. */
  submitBottom: number | null;
  /** The web view's height now. */
  viewportHeight: number;
  /** The web view's height with no keyboard up. */
  restingViewportHeight: number;
  keyboardHeight: number;
  /** How far the page is moved up right now. */
  currentLift: number;
}

/**
 * How far to move the page up so the focused field sits above the keyboard,
 * together with its form's button when both fit; never so far that the focused
 * field's top leaves the screen. When the web view has already shrunk for the
 * keyboard, the keyboard's top is the view's bottom.
 */
export const keyboardLift = ({
  focusedTop,
  focusedBottom,
  submitBottom,
  viewportHeight,
  restingViewportHeight,
  keyboardHeight,
  currentLift,
}: KeyboardLiftInput): number => {
  if (keyboardHeight <= 0) return 0;
  const keyboardTop = Math.min(viewportHeight, restingViewportHeight - keyboardHeight);
  const visibleBottom = keyboardTop - KEYBOARD_CLEARANCE_PX;
  const restingTop = focusedTop + currentLift;
  const restingBottom = focusedBottom + currentLift;
  const restingFormBottom =
    submitBottom === null ? restingBottom : Math.max(restingBottom, submitBottom + currentLift);
  const highestLift = restingTop - KEYBOARD_CLEARANCE_PX;
  return Math.max(0, Math.round(Math.min(restingFormBottom - visibleBottom, highestLift)));
};

/**
 * The web view's height with no keyboard up, updated from a new measurement.
 * A new width is a turn or a new window: take it as it is. Otherwise a change
 * only counts while the keyboard is closed, and a shrink only while no field
 * has focus (a focused field means the keyboard is on its way, and Android 14
 * and earlier shrink the view for it before the plugin reports it).
 */
export const nextRestingViewport = (
  resting: ViewportSize,
  measured: ViewportSize,
  { keyboardOpen, fieldFocused }: { keyboardOpen: boolean; fieldFocused: boolean },
): ViewportSize => {
  if (measured.width !== resting.width) return measured;
  if (keyboardOpen) return resting;
  return measured.height >= resting.height || !fieldFocused ? measured : resting;
};

export interface KeyboardScrollInput {
  /** The lift the page would need if nothing scrolled (keyboardLift's answer). */
  neededLift: number;
  /** Top of the focused field and of its scrolling box, as drawn now. */
  focusedTop: number;
  scrollerTop: number;
  /** How far the box is scrolled now, and how far it can scroll at most. */
  scrollTop: number;
  scrollMax: number;
}

/**
 * How far to scroll the focused field's scrolling box before the page is
 * moved: as far as the keyboard needs, but not so far that the field passes
 * the box's top edge or the box runs out of content. The page is then moved
 * up only for what is left, so a box that ends at the bottom of the screen
 * cannot cut off the field or its button. Negative brings back a field that
 * sits above the box's top edge.
 */
export const keyboardScroll = ({
  neededLift,
  focusedTop,
  scrollerTop,
  scrollTop,
  scrollMax,
}: KeyboardScrollInput): number => {
  const roomAboveField = focusedTop - scrollerTop - KEYBOARD_CLEARANCE_PX;
  const roomBelowContent = scrollMax - scrollTop;
  const scroll = Math.min(neededLift, roomAboveField, roomBelowContent);
  return Math.round(Math.max(-scrollTop, scroll));
};

const isTextEntry = (element: Element | null): element is HTMLElement =>
  element instanceof HTMLInputElement ||
  element instanceof HTMLTextAreaElement ||
  (element instanceof HTMLElement && element.isContentEditable);

// The lift the page is drawn with right now. Read from the page itself, not
// from the last value set: a measurement can run (a resize, the form observer)
// before React has applied that value, and the boxes it reads are as drawn.
const drawnLift = (page: HTMLElement): number => {
  const match = /translateY\(-([\d.]+)px\)/.exec(page.style.transform);
  return match ? Number(match[1]) : 0;
};

// Whether a box lets its content scroll up and down. Only the shorthand is
// filled in where the style is not fully computed (jsdom).
const scrollsVertically = (node: HTMLElement): boolean => {
  const style = getComputedStyle(node);
  return /^(auto|scroll)$/.test(style.overflowY || style.overflow);
};

// The nearest box between the field and the page (the page included) that
// scrolls its content.
const scrollingBoxOf = (field: HTMLElement, page: HTMLElement): HTMLElement | null => {
  const ancestors = (node: HTMLElement | null): HTMLElement[] =>
    !node ? [] : node === page ? [node] : [node, ...ancestors(node.parentElement)];
  return (
    ancestors(field.parentElement).find(
      (node) => scrollsVertically(node) && node.scrollHeight > node.clientHeight,
    ) ?? null
  );
};

export interface KeepFocusedFieldAboveKeyboardOptions {
  /**
   * Scroll the focused field's scrolling box first and move the page only for
   * what is left (a page whose forms sit in a scrolling box under a fixed
   * header, such as Settings > Account).
   */
  scrollFirst?: boolean;
}

const currentViewport = (): ViewportSize => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

/**
 * Keeps the focused field of an auth page above the on-screen keyboard in the
 * store apps. Returns the style for the page's outermost box: a transform that
 * moves the whole page up (background, card and anything inside it, the
 * sign-in spinners included) while the keyboard covers the focused field or
 * its form's button, and nothing at all otherwise. What shows below the moved
 * page is the app's dark body background, under the keyboard.
 */
export const useKeepFocusedFieldAboveKeyboard = (
  pageRef: RefObject<HTMLElement | null>,
  { scrollFirst = false }: KeepFocusedFieldAboveKeyboardOptions = {},
): CSSProperties => {
  const [lift, setLift] = useState(0);

  useEffect(() => {
    let keyboardHeight = 0;
    let resting = currentViewport();

    const focusedField = (): HTMLElement | null => {
      const page = pageRef.current;
      const focused = document.activeElement;
      return page && isTextEntry(focused) && page.contains(focused) ? focused : null;
    };

    const recordResting = () => {
      resting = nextRestingViewport(resting, currentViewport(), {
        keyboardOpen: keyboardHeight > 0,
        fieldFocused: focusedField() !== null,
      });
    };

    // A form whose size changes while the keyboard is up (an error line above
    // the button) is measured again. Not every web view has ResizeObserver.
    const formObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());

    // Focus on something other than a field (the button being tapped) keeps
    // the page where it is.
    function measure() {
      if (keyboardHeight <= 0) {
        setLift(0);
        return;
      }
      const page = pageRef.current;
      const field = focusedField();
      if (!page || !field) return;
      const form = field.closest('form');
      if (form) formObserver?.observe(form);
      const submit = form?.querySelector('button[type="submit"]');
      const liftNeeded = () => {
        const fieldBox = field.getBoundingClientRect();
        return keyboardLift({
          focusedTop: fieldBox.top,
          focusedBottom: fieldBox.bottom,
          submitBottom: submit ? submit.getBoundingClientRect().bottom : null,
          viewportHeight: window.innerHeight,
          restingViewportHeight: resting.height,
          keyboardHeight,
          currentLift: drawnLift(page),
        });
      };
      const scroller = scrollFirst ? scrollingBoxOf(field, page) : null;
      const scroll = scroller
        ? keyboardScroll({
            neededLift: liftNeeded(),
            focusedTop: field.getBoundingClientRect().top,
            scrollerTop: scroller.getBoundingClientRect().top,
            scrollTop: scroller.scrollTop,
            scrollMax: scroller.scrollHeight - scroller.clientHeight,
          })
        : 0;
      if (scroller && scroll !== 0) scroller.scrollTop += scroll;
      setLift(liftNeeded());
    }

    const keyboardGone = () => {
      keyboardHeight = 0;
      formObserver?.disconnect();
      setLift(0);
    };

    const onShown = (event: Event) => {
      const height = (event as KeyboardHeightEvent).keyboardHeight;
      if (typeof height !== 'number' || height <= 0) return;
      keyboardHeight = height;
      measure();
    };
    const onGone = () => {
      keyboardGone();
      recordResting();
    };
    const onResize = () => {
      recordResting();
      if (keyboardHeight > 0) measure();
    };
    // Moving from one field to the next keeps the keyboard up (no new event on
    // Android), so a field taking focus is measured again. With the keyboard
    // closed, a field taking focus is the moment to read the resting size
    // again: the keyboard has not appeared yet. The size read at mount can be
    // out of date, because the app can turn itself while this page mounts (a
    // tablet started upright turns to landscape, a phone started sideways turns
    // upright) and that turn does not always reach the page as a resize.
    const onFocusIn = () => {
      if (keyboardHeight > 0) measure();
      else recordResting();
    };
    // A turn read as a turn, for the same reason. The new size can land a
    // frame after the event, so it is read then too.
    let orientationFrame = 0;
    const onOrientationChange = () => {
      if (keyboardHeight > 0) return;
      recordResting();
      cancelAnimationFrame(orientationFrame);
      orientationFrame = requestAnimationFrame(() => {
        if (keyboardHeight <= 0) recordResting();
      });
    };
    const screenOrientation = typeof screen === 'undefined' ? undefined : screen.orientation;
    // Leaving the app with the keyboard up may never send keyboardDidHide.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') keyboardGone();
    };

    window.addEventListener(KEYBOARD_SHOWN_EVENT, onShown);
    window.addEventListener(KEYBOARD_GONE_EVENT, onGone);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrientationChange);
    screenOrientation?.addEventListener('change', onOrientationChange);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener(KEYBOARD_SHOWN_EVENT, onShown);
      window.removeEventListener(KEYBOARD_GONE_EVENT, onGone);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onOrientationChange);
      screenOrientation?.removeEventListener('change', onOrientationChange);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      cancelAnimationFrame(orientationFrame);
      formObserver?.disconnect();
    };
  }, [pageRef, scrollFirst]);

  return lift > 0 ? { transform: `translateY(-${lift}px)` } : {};
};
